// Hook: possible-repeat flags for a LIST of requests (db-211) — the Responders Task List
// and All Requests chips + filter. One database call per change of the id list (not per
// row). Best-effort: on error the map is empty and the lists simply show no chips.
//
// Pass enabled = false for anyone who isn't Zephyr staff. The id list is compared by
// value, so a live list re-rendering with the same requests doesn't re-query.

import React from "react";
import { getRepeatFlags } from "../data/repeatMatches";
import { type RepeatFlagsByRequest, toRepeatFlagMap } from "../services/repeatCheck";

const EMPTY: RepeatFlagsByRequest = {};

export function useRepeatFlags(
  requestIds: (number | string | null | undefined)[] | null | undefined,
  opts: { enabled: boolean; months?: number | null; types?: string[] | null }
): { flags: RepeatFlagsByRequest; loaded: boolean } {
  const { enabled, months } = opts;
  const idsKey = Array.from(
    new Set((requestIds || []).map((v) => Number(v)).filter((n) => Number.isFinite(n) && n > 0))
  )
    .sort((a, b) => a - b)
    .join(",");
  const typesKey = opts.types == null ? null : JSON.stringify(opts.types);
  const [state, setState] = React.useState<{ flags: RepeatFlagsByRequest; loaded: boolean }>({
    flags: EMPTY,
    loaded: false,
  });

  React.useEffect(
    function () {
      let alive = true;
      if (!enabled || idsKey === "") {
        setState({ flags: EMPTY, loaded: true });
        return;
      }
      const ids = idsKey.split(",").map(Number);
      const types: string[] | null = typesKey == null ? null : JSON.parse(typesKey);
      getRepeatFlags(ids, { months, types }).then(function (res) {
        if (!alive) return;
        setState({ flags: toRepeatFlagMap(res.rows), loaded: true });
      });
      return function () {
        alive = false;
      };
    },
    [idsKey, enabled, months, typesKey]
  );

  return state;
}
